import { ApolloClient, InMemoryCache, createHttpLink, gql } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { from } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { db, httpsCallable, functions } from '../firebase';
import { JOBBER_CONFIG } from './jobberConfig';

const JOBBER_DOC_ID = 'connection';

// Parse Jobber token response: expires_in (seconds) or expires_at (ISO string)
function parseExpiresAt(tokenData) {
  if (tokenData.expires_in != null && typeof tokenData.expires_in === 'number') {
    return Date.now() + tokenData.expires_in * 1000;
  }
  if (tokenData.expires_at && typeof tokenData.expires_at === 'string') {
    const ms = new Date(tokenData.expires_at).getTime();
    if (!isNaN(ms)) return ms;
  }
  return Date.now() + 60 * 60 * 1000; // default 1 hour
}

// In-memory cache for the current user's tokens (so Apollo auth link can read synchronously)
let _tokenCache = null;

// Current user id for refresh (set when loadTokensForUser is called)
let _currentUserId = null;

// Lock to prevent concurrent refresh attempts (devices call Cloud Function; lock prevents duplicate calls)
let _refreshInProgress = null;

// Firestore listener for connection doc (receives updates from scheduled job)
let _connectionUnsubscribe = null;

// Background refresh removed: Cloud Function scheduledJobberTokenRefresh runs every 45 min

// Create HTTP link
const httpLink = createHttpLink({
  uri: JOBBER_CONFIG.graphqlUrl,
});

// Create auth link to add access token to requests (reads from in-memory cache)
const authLink = setContext((_, { headers }) => {
  const token = JobberService.getAccessToken();
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
      'Content-Type': 'application/json',
      'X-JOBBER-GRAPHQL-VERSION': '2023-11-15'
    }
  }
});

// Retry on 401: refresh token and retry the operation
const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  const status = networkError?.statusCode ?? networkError?.response?.status;
  const is401 = status === 401 ||
    (graphQLErrors?.some?.(e => e.extensions?.code === 'UNAUTHENTICATED' || e.message?.includes?.('Invalid Token')));
  if (!is401 || !_tokenCache?.refreshToken) return forward(operation);
  const userId = _currentUserId;
  const doRefresh = () => JobberService.refreshTokens(userId).then(() => true, () => false);
  return from(doRefresh()).pipe(
    mergeMap((success) => {
      if (!success) throw networkError || new Error('Token refresh failed');
      return forward(operation);
    })
  );
});

// Create Apollo Client
export const jobberClient = new ApolloClient({
  link: errorLink.concat(authLink).concat(httpLink),
  cache: new InMemoryCache()
});

// GraphQL Queries and Mutations
export const JOBBER_QUERIES = {
  // Get Jobber account (for APP_DISCONNECT webhook mapping)
  GET_ACCOUNT: gql`
    query GetAccount {
      account {
        id
        name
      }
    }
  `,

  // Get all clients - Updated for Jobber API
  GET_CUSTOMERS: gql`
    query GetClients($first: Int!, $after: String) {
      clients(first: $first, after: $after) {
        nodes {
          id
          firstName
          lastName
          emails {
            address
          }
          phones {
            number
          }
          billingAddress {
            street1
            city
            province
            postalCode
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
        totalCount
      }
    }
  `,

  // Get customer by ID
  GET_CUSTOMER: gql`
    query GetCustomer($id: ID!) {
      customer(id: $id) {
        id
        name
        email
        phone
        address {
          street1
          city
          province
          postalCode
          country
        }
        createdAt
        updatedAt
      }
    }
  `,

  // Get all quotes
  GET_QUOTES: gql`
    query GetQuotes {
      quotes {
        id
        customer {
          id
          name
          email
          phone
        }
        status
        total
        createdAt
        updatedAt
        lineItems {
          id
          description
          quantity
          unitPrice
          totalPrice
        }
      }
    }
  `,

  // Get quote by ID
  GET_QUOTE: gql`
    query GetQuote($id: ID!) {
      quote(id: $id) {
        id
        customer {
          id
          name
          email
          phone
          address {
            street1
            city
            province
            postalCode
            country
          }
        }
        status
        total
        createdAt
        updatedAt
        lineItems {
          id
          description
          quantity
          unitPrice
          totalPrice
        }
      }
    }
  `
};

export const JOBBER_MUTATIONS = {
  // Create a new client with property - USING EXACT CODE FROM LATEST PROVIDED EXAMPLE
  CREATE_CUSTOMER: gql`
    mutation CreateClient($input: ClientCreateInput!) {
      clientCreate(input: $input) {
        client {
          id
          name
          properties {
            id
            address {
              street1
              city
              province
              postalCode
              country
            }
          }
        }
        userErrors {
          path
          message
        }
      }
    }
  `,

  // Update customer
  UPDATE_CUSTOMER: gql`
    mutation UpdateCustomer($id: ID!, $input: CustomerInput!) {
      customerUpdate(id: $id, input: $input) {
        customer {
          id
          name
          email
          phone
          address {
            street1
            city
            province
            postalCode
            country
          }
        }
        userErrors {
          field
          message
        }
      }
    }
  `,

  // Create a new quote - USING EXACT CODE FROM LATEST PROVIDED EXAMPLE
  CREATE_QUOTE: gql`
    mutation CreateQuote($input: QuoteCreateInput!) {
      quoteCreate(input: $input) {
        quote {
          id
          title
          property {
            id
          }
        }
        userErrors {
          path
          message
        }
      }
    }
  `,

  // Update quote
  UPDATE_QUOTE: gql`
    mutation UpdateQuote($id: ID!, $input: QuoteInput!) {
      quoteUpdate(id: $id, input: $input) {
        quote {
          id
          customer {
            id
            name
            email
            phone
          }
          status
          total
          createdAt
          lineItems {
            id
            description
            quantity
            unitPrice
            totalPrice
          }
        }
        userErrors {
          field
          message
        }
      }
    }
  `,

  // Create quote note - using QuoteCreateNoteInput as suggested by error
  CREATE_QUOTE_NOTE: gql`
    mutation CreateQuoteNote($quoteId: EncodedId!, $input: QuoteCreateNoteInput!) {
      quoteCreateNote(quoteId: $quoteId, input: $input) {
        quoteNote {
          id
          message
        }
        userErrors {
          path
          message
        }
      }
    }
  `
};

// Service functions
export class JobberService {
  // Tokens are stored in Firestore at users/{userId}/jobber/connection and in-memory cache.
  // loadTokensForUser(userId) loads from Firestore into cache when user logs in.
  // storeTokens(tokenData, userId) writes to Firestore and cache; clearTokens(userId) removes from both.
  // Apollo auth link reads from cache via getAccessToken(). isAuthenticatedWithRefresh(userId) refreshes when needed.

  /** Load tokens from Firestore into in-memory cache. Call when user logs in. */
  static async loadTokensForUser(userId) {
    if (!userId) {
      _tokenCache = null;
      _currentUserId = null;
      this._stopConnectionListener();
      return;
    }
    _currentUserId = userId;
    this._stopConnectionListener();
    try {
      const ref = doc(db, 'users', userId, 'jobber', JOBBER_DOC_ID);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        _tokenCache = null;
        return;
      }
      const data = snap.data();
      if (!data.accessToken || !data.refreshToken || data.expiresAt == null) {
        _tokenCache = null;
        return;
      }
      _tokenCache = {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        expiresAt: typeof data.expiresAt === 'number' ? data.expiresAt : Number(data.expiresAt)
      };
      console.log('✅ Jobber tokens loaded from Firestore for user');
      this._startConnectionListener(userId);
      const fifteenMinutesFromNow = Date.now() + 15 * 60 * 1000;
      if (_tokenCache.expiresAt <= fifteenMinutesFromNow) {
        console.log('🔄 Token expires soon, proactively refreshing on load...');
        await this.refreshTokens(userId).catch(() => {});
      }
    } catch (error) {
      console.error('❌ Error loading Jobber tokens from Firestore:', error);
      _tokenCache = null;
      this.stopBackgroundRefresh();
    }
  }

  /** Start Firestore listener for connection doc (receives updates from scheduled job). */
  static _startConnectionListener(userId) {
    if (!userId) return;
    this._stopConnectionListener();
    const ref = doc(db, 'users', userId, 'jobber', JOBBER_DOC_ID);
    _connectionUnsubscribe = onSnapshot(ref, (snap) => {
      if (!snap.exists() || _currentUserId !== userId) return;
      const data = snap.data();
      if (!data.accessToken || !data.refreshToken || data.expiresAt == null) {
        _tokenCache = null;
        return;
      }
      const expiresAt = typeof data.expiresAt === 'number' ? data.expiresAt : Number(data.expiresAt);
      if (!_tokenCache || expiresAt > _tokenCache.expiresAt) {
        _tokenCache = {
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          expiresAt
        };
        console.log('✅ Jobber tokens updated from Firestore (scheduled refresh)');
      }
    }, (err) => {
      console.error('❌ Jobber connection listener error:', err);
    });
  }

  /** Stop Firestore listener for connection doc. */
  static _stopConnectionListener() {
    if (_connectionUnsubscribe) {
      _connectionUnsubscribe();
      _connectionUnsubscribe = null;
    }
  }

  /** No-op: background refresh is handled by Cloud Function scheduledJobberTokenRefresh every 45 min */
  static startBackgroundRefresh() {}
  static stopBackgroundRefresh() {}

  /** Clear in-memory cache only (e.g. on logout). Does not delete from Firestore. */
  static clearTokenCache() {
    _tokenCache = null;
    _currentUserId = null;
    _refreshInProgress = null;
    this._stopConnectionListener();
    this.stopBackgroundRefresh();
  }

  // Check if user is authenticated (from cache; does not refresh)
  static isAuthenticated() {
    if (!_tokenCache || !_tokenCache.accessToken) return false;
    const now = Date.now();
    if (now >= _tokenCache.expiresAt) return false; // expired; don't clear here, let refresh try
    return true;
  }

  // Check if user is authenticated with automatic token refresh. Pass userId for storing new tokens.
  static async isAuthenticatedWithRefresh(userId) {
    if (!_tokenCache && userId) {
      await this.loadTokensForUser(userId);
    }
    if (!_tokenCache || !_tokenCache.accessToken || !_tokenCache.refreshToken) return false;

    const now = Date.now();
    const fifteenMinutesFromNow = now + (15 * 60 * 1000);

    if (now >= _tokenCache.expiresAt) {
      console.log('🔄 Access token expired, attempting refresh...');
      try {
        const success = await this.refreshTokens(userId);
        return success;
      } catch (error) {
        console.error('❌ Token refresh failed:', error);
        if (userId) this.clearTokens(userId);
        return false;
      }
    }
    if (fifteenMinutesFromNow >= _tokenCache.expiresAt) {
      console.log('🔄 Access token expires soon, proactively refreshing...');
      try {
        const success = await this.refreshTokens(userId);
        return success;
      } catch (error) {
        console.error('❌ Proactive token refresh failed:', error);
        // Proactive refresh failed (e.g. invalid refresh token). Clear immediately
        // so user shows disconnected rather than waiting for next failed API call.
        if (userId) this.clearTokens(userId);
        return false;
      }
    }
    return true;
  }

  // Refresh access token via Cloud Function (centralized, no per-device race).
  // Uses _refreshInProgress lock so only one in-flight call per device.
  static async refreshTokens(userId) {
    if (_refreshInProgress) {
      try {
        await _refreshInProgress;
        return true;
      } catch (e) {
        throw e;
      }
    }
    if (!userId) throw new Error('userId required for refresh');
    if (!_tokenCache?.refreshToken) throw new Error('No refresh token available');

    const doRefresh = async () => {
      console.log('🔄 Refreshing access token via Cloud Function...');
      const refreshJobberToken = httpsCallable(functions, 'refreshJobberToken');
      const { data } = await refreshJobberToken({ userId });

      if (data?.success && data?.accessToken && data?.refreshToken) {
        _tokenCache = {
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          expiresAt: typeof data.expiresAt === 'number' ? data.expiresAt : Number(data.expiresAt)
        };
        console.log('✅ Token refresh successful');
        return true;
      }
      throw new Error('Invalid refresh response');
    };

    _refreshInProgress = doRefresh()
      .catch((err) => { throw err; })
      .finally(() => { _refreshInProgress = null; });
    return _refreshInProgress;
  }

  // Check if auto-sync is enabled (backward compatibility - checks syncAll)
  static isAutoSyncEnabled() {
    return this.isSyncAllEnabled();
  }

  // Device-specific sync preferences (platform: "browser" or "ios")
  static getPlatform() {
    return 'browser';
  }

  // Sync All Quotes preference
  static isSyncAllEnabled() {
    return localStorage.getItem(`jobber_sync_all_${this.getPlatform()}`) === 'true';
  }

  static async setSyncAllEnabled(enabled, userId = null, skipConfirmation = false) {
    // If enabling, require confirmation unless explicitly skipped
    if (enabled && !skipConfirmation) {
      // This will be handled by the UI with a confirmation dialog
      // For now, we'll just update the preference
      localStorage.setItem(`jobber_sync_all_${this.getPlatform()}`, enabled.toString());
    } else {
      localStorage.setItem(`jobber_sync_all_${this.getPlatform()}`, enabled.toString());
    }
    
    // Update Firestore if userId provided
    if (userId) {
      await this.updateSyncPreferencesInFirestore(userId);
    }
  }

  // Sync Individual Quotes preference
  static isSyncIndividualEnabled() {
    return localStorage.getItem(`jobber_sync_individual_${this.getPlatform()}`) === 'true';
  }

  static async setSyncIndividualEnabled(enabled, userId = null) {
    localStorage.setItem(`jobber_sync_individual_${this.getPlatform()}`, enabled.toString());
    
    // Update Firestore if userId provided
    if (userId) {
      await this.updateSyncPreferencesInFirestore(userId);
    }
  }

  // Update sync preferences in Firestore
  static async updateSyncPreferencesInFirestore(userId) {
    if (!userId) return;

    try {
      const prefsRef = doc(db, 'users', userId, 'jobber', 'sync_preferences', this.getPlatform());
      await setDoc(prefsRef, {
        syncAll: this.isSyncAllEnabled(),
        syncIndividual: this.isSyncIndividualEnabled(),
        platform: this.getPlatform(),
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      console.error('Error updating sync preferences in Firestore:', error);
    }
  }

  // Load sync preferences from Firestore
  static async loadSyncPreferencesFromFirestore(userId) {
    if (!userId) return;

    try {
      // First, check if we need to migrate from old autoSync preference
      const oldAutoSyncKey = 'jobber_auto_sync';
      const hasOldPreference = localStorage.getItem(oldAutoSyncKey) !== null;
      const oldAutoSyncValue = localStorage.getItem(oldAutoSyncKey) === 'true';

      const prefsRef = doc(db, 'users', userId, 'jobber', 'sync_preferences', this.getPlatform());
      const prefsSnap = await getDoc(prefsRef);

      if (prefsSnap.exists()) {
        const data = prefsSnap.data();
        localStorage.setItem(`jobber_sync_all_${this.getPlatform()}`, (data.syncAll || false).toString());
        localStorage.setItem(`jobber_sync_individual_${this.getPlatform()}`, (data.syncIndividual || false).toString());

        // Migrate old preference if it exists and Firestore doesn't have syncAll set
        if (hasOldPreference && !data.syncAll && oldAutoSyncValue) {
          await this.setSyncAllEnabled(true, true);
        }
      } else if (hasOldPreference) {
        // No Firestore preferences, migrate old autoSync to new structure
        await this.setSyncAllEnabled(oldAutoSyncValue, true);
        await this.setSyncIndividualEnabled(false);
      }
    } catch (error) {
      console.error('Error loading sync preferences from Firestore:', error);
    }
  }


  // Get access token from in-memory cache (for Apollo auth link)
  static getAccessToken() {
    return _tokenCache?.accessToken ?? null;
  }

  // Store tokens in Firestore and in-memory cache. Requires userId.
  // Also fetches Jobber account id and creates jobberAccountToUser index for APP_DISCONNECT webhook.
  static async storeTokens(tokenData, userId) {
    if (!tokenData.access_token) throw new Error('Missing access_token in token data');
    if (!tokenData.refresh_token) throw new Error('Missing refresh_token in token data');
    if (!userId) throw new Error('userId required to store Jobber tokens in Firestore');

    const expiresAt = parseExpiresAt(tokenData);
    _tokenCache = {
      accessToken: tokenData.access_token,
      refreshToken: tokenData.refresh_token,
      expiresAt
    };

    const ref = doc(db, 'users', userId, 'jobber', JOBBER_DOC_ID);
    let accountId = null;
    try {
      const { data } = await jobberClient.query({ query: JOBBER_QUERIES.GET_ACCOUNT });
      accountId = data?.account?.id || null;
    } catch (err) {
      console.warn('Could not fetch Jobber account for webhook index:', err);
    }
    await setDoc(ref, {
      accessToken: tokenData.access_token,
      refreshToken: tokenData.refresh_token,
      expiresAt,
      ...(accountId && { accountId }),
      updatedAt: serverTimestamp()
    });
    if (accountId) {
      await setDoc(doc(db, 'jobberAccountToUser', accountId), { userId }, { merge: true });
    }
    console.log('✅ Jobber tokens stored in Firestore');
  }

  // Clear tokens from Firestore and in-memory cache. Also removes jobberAccountToUser index.
  static async clearTokens(userId) {
    _tokenCache = null;
    if (!userId) return;
    try {
      const ref = doc(db, 'users', userId, 'jobber', JOBBER_DOC_ID);
      const snap = await getDoc(ref);
      const accountId = snap.exists() ? snap.data()?.accountId : null;
      await deleteDoc(ref);
      if (accountId) {
        await deleteDoc(doc(db, 'jobberAccountToUser', accountId));
      }
      console.log('✅ Jobber tokens removed from Firestore');
    } catch (error) {
      console.error('❌ Error clearing Jobber tokens from Firestore:', error);
    }
  }

  // Test Jobber connection (simple test without fetching data). Pass userId for token refresh.
  static async testConnection(userId) {
    try {
      const isAuthenticated = await this.isAuthenticatedWithRefresh(userId);
      if (!isAuthenticated) {
        throw new Error('Not connected to Jobber');
      }
      
      console.log('🧪 Testing Jobber GraphQL connection...');
      const testQuery = gql`
        query TestQuery {
          clients(first: 1) {
            totalCount
          }
        }
      `;
      
      const { data } = await jobberClient.query({ query: testQuery });
      console.log('✅ Jobber connection test successful!');
      return { 
        success: true, 
        message: `Successfully connected to Jobber. Found ${data.clients.totalCount} clients in your account.` 
      };
    } catch (error) {
      console.error('❌ Connection test failed:', error);
      throw error;
    }
  }

  // Get customers from Jobber. Pass userId for token refresh; required if getCustomers is ever used.
  static async getCustomers(userId = null) {
    if (userId) {
      const isAuthenticated = await this.isAuthenticatedWithRefresh(userId);
      if (!isAuthenticated) throw new Error('Not authenticated with Jobber. Please reconnect.');
    }
    try {
      console.log('🔍 Fetching ALL customers from Jobber with pagination...');
      
      let allCustomers = [];
      let hasNextPage = true;
      let after = null;
      let pageCount = 0;
      
      while (hasNextPage) {
        pageCount++;
        console.log(`📄 Fetching page ${pageCount}...`);
        
        const { data } = await jobberClient.query({
          query: JOBBER_QUERIES.GET_CUSTOMERS,
          variables: {
            first: 50, // Fetch 50 at a time for efficiency
            after: after
          }
        });
        
        console.log(`📋 Page ${pageCount} response:`, {
          nodesCount: data.clients.nodes.length,
          totalCount: data.clients.totalCount,
          hasNextPage: data.clients.pageInfo.hasNextPage
        });
        
        // Extract clients from this page
        const pageCustomers = data.clients.nodes.map(client => ({
          id: client.id,
          name: `${client.firstName} ${client.lastName}`.trim(),
          email: client.emails && client.emails.length > 0 ? client.emails[0].address : '',
          phone: client.phones && client.phones.length > 0 ? client.phones[0].number : '',
          address: client.billingAddress ? 
            `${client.billingAddress.street1}, ${client.billingAddress.city}, ${client.billingAddress.province} ${client.billingAddress.postalCode}`.trim() : 
            ''
        }));
        
        allCustomers = [...allCustomers, ...pageCustomers];
        hasNextPage = data.clients.pageInfo.hasNextPage;
        after = data.clients.pageInfo.endCursor;
        
        console.log(`✅ Page ${pageCount} processed: ${pageCustomers.length} customers (Total so far: ${allCustomers.length})`);
      }
      
      console.log(`🎯 COMPLETE: Fetched ${allCustomers.length} total customers across ${pageCount} pages`);
      console.log('👥 All customers:', allCustomers);
      
      return allCustomers;
    } catch (error) {
      console.error('❌ Error fetching customers:', error);
      console.error('❌ Error details:', error.message);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error network:', error.networkError);
      
      // Try to get more detailed error information
      if (error.networkError) {
        console.error('🌐 Network error details:', error.networkError);
        if (error.networkError.result) {
          console.error('📄 Error result:', error.networkError.result);
        }
      }
      
      // Try a simpler query to test basic connectivity
      try {
        console.log('🧪 Testing basic GraphQL connectivity...');
        const testQuery = gql`
          query TestQuery {
            clients(first: 1) {
              totalCount
            }
          }
        `;
        const testResult = await jobberClient.query({ query: testQuery });
        console.log('✅ Basic GraphQL connection works:', testResult);
      } catch (testError) {
        console.error('❌ Basic GraphQL test failed:', testError);
        console.error('❌ Test error details:', testError.message);
        if (testError.networkError) {
          console.error('❌ Test network error:', testError.networkError);
        }
      }
      
      throw error;
    }
  }

  // Create client with property in Jobber - USING EXACT CODE FROM LATEST PROVIDED EXAMPLE
  static async createCustomer(customerData) {
    try {
      // Parse address from customerData.address string
      const addressParts = (customerData.address || '').split(',').map(part => part.trim());
      
      // Use the exact input structure from the latest provided code
      const clientInput = {
        name: customerData.name,
        properties: [
          {
            address: {
              street1: addressParts[0] || "123 Main St",
              city: addressParts[1] || "Unknown",
              province: addressParts[2] || "Unknown", 
              postalCode: addressParts[3] || "00000",
              country: "USA"
            }
          }
        ]
      };

      console.log('Client input with properties array:', clientInput);

      const { data } = await jobberClient.mutate({
        mutation: JOBBER_MUTATIONS.CREATE_CUSTOMER,
        variables: { input: clientInput }
      });
      
      if (data.clientCreate.userErrors.length > 0) {
        console.error("❌ Error with customer:", data.clientCreate.userErrors);
        throw new Error(`Failed to create client: ${data.clientCreate.userErrors.map(e => `${e.path}: ${e.message}`).join(", ")}`);
      }

      const createdClient = data.clientCreate.client;
      const propertyId = createdClient.properties?.[0]?.id;

      if (!propertyId) {
        console.error("❌ No property was created for client:", createdClient);
        throw new Error("No property was created for client");
      }
      
      return {
        id: createdClient.id,
        name: createdClient.name,
        propertyId: propertyId // Return the property ID for quote creation
      };
    } catch (error) {
      console.error('Error creating customer with property:', error);
      throw error;
    }
  }

  // Get customer property ID
  static async getCustomerPropertyId(customerId) {
    try {
      const query = gql`
        query GetClientProperties($clientId: EncodedId!) {
          client(id: $clientId) {
            properties {
              id
              name
            }
          }
        }
      `;
      
      const { data } = await jobberClient.query({
        query,
        variables: { clientId: customerId }
      });
      
      if (data.client.properties.length > 0) {
        return data.client.properties[0].id;
      }
      
      throw new Error('No properties found for client');
    } catch (error) {
      console.error('Error getting property ID:', error);
      throw error;
    }
  }

  // Parse address string into structured format with enhanced robustness
  static parseAddressString(addressString) {
    if (!addressString || typeof addressString !== 'string') {
      console.log('⚠️ No address string provided, using minimal address');
      return {
        street1: "123 Main St",
        city: "Unknown",
        province: "Unknown",
        postalCode: "00000",
        country: "US"
      };
    }

    console.log('📍 Parsing address string:', addressString);
    
    // Enhanced parsing for different address formats
    let parts;
    
    // Try different splitting methods based on common address formats
    if (addressString.includes(',')) {
      // Format: "123 Main St, City, State ZIP, Country"
      parts = addressString.split(',').map(part => part.trim()).filter(part => part);
    } else if (addressString.match(/\d+\s+.*\s+\w{2}\s+\d{5}/)) {
      // Format: "123 Main St City State ZIP" (no commas)
      const match = addressString.match(/^(\d+\s+.*?)\s+([A-Za-z\s]+)\s+([A-Za-z]{2})\s+(\d{5}(-\d{4})?)$/);
      if (match) {
        parts = [match[1], match[2], `${match[3]} ${match[4]}`];
      } else {
        parts = addressString.split(/\s+/);
      }
    } else {
      // Fallback: split by spaces
      parts = addressString.split(/\s+/).filter(part => part);
    }
    
    const result = {
      street1: "123 Main St",
      city: "Unknown", 
      province: "Unknown",
      postalCode: "00000",
      country: "US"
    };

    // Enhanced validation with better error messages
    if (parts.length < 2) {
      console.log('⚠️ Insufficient address data (need at least street and city), using minimal address');
      console.log('⚠️ Address parts found:', parts);
      return result;
    }

    // First part is street address - enhanced validation
    if (parts.length >= 1) {
      const street = parts[0];
      // Check if it's too vague or doesn't contain a number
      if (street.length < 5 || !/\d/.test(street)) {
        console.log('⚠️ Street address too vague or missing number, using default');
        console.log('⚠️ Street provided:', street);
        result.street1 = "123 Main St";
      } else {
        result.street1 = street;
        console.log('✅ Street address validated:', street);
      }
    }

    // Second part is city - enhanced validation
    if (parts.length >= 2) {
      const city = parts[1];
      if (city.length < 2) {
        console.log('⚠️ City name too short, using default');
        result.city = "Unknown";
      } else {
        result.city = city;
        console.log('✅ City validated:', city);
      }
    }

    // Third part might be "State ZIP" or just "State" - enhanced parsing
    if (parts.length >= 3) {
      const stateZipPart = parts[2];
      console.log('🔍 Processing state/zip part:', stateZipPart);
      
      // Try to match "State ZIP" pattern (e.g., "Idaho 83854" or "ID 83854")
      const stateZipMatch = stateZipPart.match(/^([A-Za-z\s]+)\s+(\d{5}(-\d{4})?)$/);
      if (stateZipMatch) {
        result.province = stateZipMatch[1].trim();
        result.postalCode = stateZipMatch[2];
        console.log('✅ State/ZIP parsed:', { state: result.province, zip: result.postalCode });
      } else {
        // If no ZIP found, treat the whole thing as state
        result.province = stateZipPart;
        console.log('⚠️ No ZIP code found, treating as state only:', stateZipPart);
      }
    }

    // Fourth part might be country
    if (parts.length >= 4) {
      result.country = parts[3];
      console.log('✅ Country set:', result.country);
    }

    // Enhanced validation with specific error messages
    if (result.street1 === "123 Main St" && addressString !== "123 Main St") {
      console.log('⚠️ Address validation failed - street address not properly parsed');
      console.log('⚠️ Original address:', addressString);
      console.log('⚠️ Parsed parts:', parts);
    }

    // Additional validation for common issues
    if (result.city === "Unknown" && parts.length >= 2) {
      console.log('⚠️ City parsing failed - check address format');
    }

    if (result.province === "Unknown" && parts.length >= 3) {
      console.log('⚠️ State parsing failed - check address format');
    }

    console.log('📍 Parsed address result:', result);
    return result;
  }

  // Create a property for a client using quote address data - JACKHAMMER APPROACH
  static async createPropertyForClient(clientId, addressData = null) {
    console.log('🔨 JACKHAMMER: Trying multiple GraphQL mutation structures...');
    
    // Parse address data if provided
    const addressFields = this.parseAddressString(addressData);
    console.log('📍 Parsed address fields:', addressFields);

    // Define multiple mutation structures to try - SMART JACKHAMMER v2
    const mutationAttempts = [
      // Attempt 1: Direct clientId + input wrapper (based on error patterns)
      {
        name: "Direct clientId + Input Wrapper",
        mutation: gql`
          mutation CreateProperty($clientId: EncodedId!, $input: PropertyCreateInput!) {
            propertyCreate(clientId: $clientId, input: $input) {
              properties {
                id
                address {
                  street1
                  city
                  province
                  postalCode
                  country
                }
              }
              userErrors {
                message
                path
              }
            }
          }
        `,
        variables: (clientId, address) => ({
          clientId: clientId,
          input: {
            address: address
          }
        })
      },
      // Attempt 2: PropertyEditInput (Jobber suggested this)
      {
        name: "PropertyEditInput as suggested by Jobber",
        mutation: gql`
          mutation CreateProperty($clientId: EncodedId!, $input: PropertyEditInput!) {
            propertyCreate(clientId: $clientId, input: $input) {
              properties {
                id
                address {
                  street1
                  city
                  province
                  postalCode
                  country
                }
              }
              userErrors {
                message
                path
              }
            }
          }
        `,
        variables: (clientId, address) => ({
          clientId: clientId,
          input: {
            address: address
          }
        })
      },
      // Attempt 3: Different address input type
      {
        name: "PropertyAddressCreateInput",
        mutation: gql`
          mutation CreateProperty($clientId: EncodedId!, $input: PropertyCreateInput!) {
            propertyCreate(clientId: $clientId, input: $input) {
              properties {
                id
                address {
                  street1
                  city
                  province
                  postalCode
                  country
                }
              }
              userErrors {
                message
                path
              }
            }
          }
        `,
        variables: (clientId, address) => ({
          clientId: clientId,
          input: {
            address: address
          }
        })
      },
      // Attempt 4: Minimal required fields only
      {
        name: "Minimal Required Fields",
        mutation: gql`
          mutation CreateProperty($clientId: EncodedId!, $input: PropertyCreateInput!) {
            propertyCreate(clientId: $clientId, input: $input) {
              properties {
                id
              }
              userErrors {
                message
                path
              }
            }
          }
        `,
        variables: (clientId, address) => ({
          clientId: clientId,
          input: {
            address: {
              street1: address.street1,
              city: address.city
            }
          }
        })
      },
      // Attempt 5: Different field names (state instead of province)
      {
        name: "State instead of Province",
        mutation: gql`
          mutation CreateProperty($clientId: EncodedId!, $input: PropertyCreateInput!) {
            propertyCreate(clientId: $clientId, input: $input) {
              properties {
                id
                address {
                  street1
                  city
                  state
                  postalCode
                  country
                }
              }
              userErrors {
                message
                path
              }
            }
          }
        `,
        variables: (clientId, address) => ({
          clientId: clientId,
          input: {
            address: {
              street1: address.street1,
              city: address.city,
              state: address.province,
              postalCode: address.postalCode,
              country: address.country
            }
          }
        })
      },
      // Attempt 6: Different address structure
      {
        name: "Different Address Structure",
        mutation: gql`
          mutation CreateProperty($clientId: EncodedId!, $input: PropertyCreateInput!) {
            propertyCreate(clientId: $clientId, input: $input) {
              properties {
                id
                address {
                  street1
                  city
                  state
                  zip
                  country
                }
              }
              userErrors {
                message
                path
              }
            }
          }
        `,
        variables: (clientId, address) => ({
          clientId: clientId,
          input: {
            address: {
              street1: address.street1,
              city: address.city,
              state: address.province,
              zip: address.postalCode,
              country: address.country
            }
          }
        })
      },
      // Attempt 7: No address at all (minimal property)
      {
        name: "No Address - Minimal Property",
        mutation: gql`
          mutation CreateProperty($clientId: EncodedId!) {
            propertyCreate(clientId: $clientId) {
              properties {
                id
              }
              userErrors {
                message
                path
              }
            }
          }
        `,
        variables: (clientId, address) => ({
          clientId: clientId
        })
      },
      // Attempt 8: Different mutation name
      {
        name: "Different Mutation Name",
        mutation: gql`
          mutation CreateProperty($clientId: EncodedId!, $input: PropertyCreateInput!) {
            propertyCreate(clientId: $clientId, input: $input) {
              properties {
                id
                address {
                  street1
                  city
                  province
                  postalCode
                  country
                }
              }
              userErrors {
                message
                path
              }
            }
          }
        `,
        variables: (clientId, address) => ({
          clientId: clientId,
          input: {
            address: address
          }
        })
      },
      // Attempt 9: Different input type name
      {
        name: "PropertyInput instead of PropertyCreateInput",
        mutation: gql`
          mutation CreateProperty($clientId: EncodedId!, $input: PropertyInput!) {
            propertyCreate(clientId: $clientId, input: $input) {
              properties {
                id
                address {
                  street1
                  city
                  province
                  postalCode
                  country
                }
              }
              userErrors {
                message
                path
              }
            }
          }
        `,
        variables: (clientId, address) => ({
          clientId: clientId,
          input: {
            address: address
          }
        })
      },
      // Attempt 10: Different address input type
      {
        name: "AddressInput instead of PropertyAddress",
        mutation: gql`
          mutation CreateProperty($clientId: EncodedId!, $input: PropertyCreateInput!) {
            propertyCreate(clientId: $clientId, input: $input) {
              properties {
                id
                address {
                  street1
                  city
                  province
                  postalCode
                  country
                }
              }
              userErrors {
                message
                path
              }
            }
          }
        `,
        variables: (clientId, address) => ({
          clientId: clientId,
          input: {
            address: address
          }
        })
      }
    ];

    // Try each mutation structure
    for (let i = 0; i < mutationAttempts.length; i++) {
      const attempt = mutationAttempts[i];
      console.log(`🔨 Attempt ${i + 1}/${mutationAttempts.length}: ${attempt.name}`);
      
      try {
        const variables = attempt.variables(clientId, addressFields);
        console.log('📝 Variables:', variables);
        
        const { data } = await jobberClient.mutate({
          mutation: attempt.mutation,
          variables: variables
        });
        
        console.log('📋 Response:', data);
        
        if (data.propertyCreate.userErrors && data.propertyCreate.userErrors.length > 0) {
          console.log(`❌ ${attempt.name} failed with user errors:`, data.propertyCreate.userErrors);
          continue; // Try next attempt
        }
        
        // Check for property in response (handle both 'property' and 'properties')
        let propertyId = null;
        if (data.propertyCreate.properties && data.propertyCreate.properties.length > 0) {
          propertyId = data.propertyCreate.properties[0].id;
        } else if (data.propertyCreate.property && data.propertyCreate.property.id) {
          propertyId = data.propertyCreate.property.id;
        }
        
        if (propertyId) {
          console.log(`✅ SUCCESS with ${attempt.name}! Property ID:`, propertyId);
          return propertyId;
        } else {
          console.log(`❌ ${attempt.name} succeeded but no property ID found`);
          continue; // Try next attempt
        }
        
      } catch (error) {
        console.log(`❌ ${attempt.name} failed:`, error.message);
        continue; // Try next attempt
      }
    }

    // If all attempts failed, try with minimal address
    console.log('🔨 All structured attempts failed, trying minimal address...');
    const minimalAddress = {
      street1: "123 Main St",
      city: "Unknown",
      province: "Unknown",
      postalCode: "00000",
      country: "US"
    };

    for (let i = 0; i < mutationAttempts.length; i++) {
      const attempt = mutationAttempts[i];
      console.log(`🔨 Minimal Attempt ${i + 1}/${mutationAttempts.length}: ${attempt.name}`);
      
      try {
        const variables = attempt.variables(clientId, minimalAddress);
        console.log('📝 Minimal Variables:', variables);
        
        const { data } = await jobberClient.mutate({
          mutation: attempt.mutation,
          variables: variables
        });
        
        console.log('📋 Minimal Response:', data);
        
        if (data.propertyCreate.userErrors && data.propertyCreate.userErrors.length > 0) {
          console.log(`❌ Minimal ${attempt.name} failed with user errors:`, data.propertyCreate.userErrors);
          continue;
        }
        
        let propertyId = null;
        if (data.propertyCreate.properties && data.propertyCreate.properties.length > 0) {
          propertyId = data.propertyCreate.properties[0].id;
        } else if (data.propertyCreate.property && data.propertyCreate.property.id) {
          propertyId = data.propertyCreate.property.id;
        }
        
        if (propertyId) {
          console.log(`✅ MINIMAL SUCCESS with ${attempt.name}! Property ID:`, propertyId);
          return propertyId;
        }
        
      } catch (error) {
        console.log(`❌ Minimal ${attempt.name} failed:`, error.message);
        continue;
      }
    }

    // If everything failed
    throw new Error('All property creation attempts failed - Jobber API structure unknown');
  }

  // Create quote in Jobber - USING EXACT CODE FROM LATEST PROVIDED EXAMPLE
  static async createQuote(quoteData) {
    try {
      console.log('Creating Jobber quote with data:', quoteData);
      
      // Map our line items to the format expected by Jobber
      const parsedLineItems = quoteData.lineItems.map(item => ({
        name: `${item.windowType} - ${item.counts}`,
        description: `${item.windowType} - ${item.counts}` || "",
        quantity: item.totalCount || 1,
        unitCost: {
          amount: (item.totalCount > 0 ? item.price / item.totalCount : 0).toString(),
          currency: "USD"
        }
      }));

      const { data } = await jobberClient.mutate({
        mutation: JOBBER_MUTATIONS.CREATE_QUOTE,
        variables: {
          input: {
            clientId: quoteData.customerId,
            propertyId: quoteData.propertyId,
            title: `Window Cleaning Quote - ${new Date().toLocaleDateString()}`,
            message: "Professional window cleaning services" || "",
            lineItems: parsedLineItems
          }
        }
      });

      if (data.quoteCreate.userErrors.length > 0) {
        console.error("❌ Error syncing quote to Jobber:", data.quoteCreate.userErrors);
        throw new Error(`Failed to create quote: ${data.quoteCreate.userErrors.map(e => `${e.path}: ${e.message}`).join(", ")}`);
      }

      console.log("✅ Successfully created quote in Jobber:", data.quoteCreate.quote);

      return data.quoteCreate.quote;
    } catch (error) {
      console.error("❌ Error syncing to Jobber:", error);
      throw error;
    }
  }

  // SMART SYNC: Handles existing clients and properties
  static async syncQuoteToJobber(quote) {
    console.log('🧠 SMART SYNC: Checking for existing clients and properties...');
    console.log('📋 Quote data:', quote);
    
    return await this.smartSync(quote);
  }

  // Smart sync with 3 scenarios: New/New/New, Existing/New/New, Existing/Existing/New
  static async smartSync(quote) {
    console.log('🧠 Starting smart sync with client/property detection...');
    const userId = quote.userId;
    const isAuthenticated = await this.isAuthenticatedWithRefresh(userId);
    if (!isAuthenticated) {
      throw new Error('Not authenticated with Jobber. Please reconnect.');
    }
    
    // Parse customer name - use firstName/lastName if available, otherwise parse from name field
    let firstName = quote.customer.firstName || '';
    let lastName = quote.customer.lastName || '';
    
    // Fallback: parse from name field for backward compatibility with old quotes
    if (!firstName && quote.customer.name) {
      const nameParts = quote.customer.name.trim().split(/\s+/);
      if (nameParts.length === 1) {
        firstName = nameParts[0];
        lastName = '';
      } else {
        firstName = nameParts.slice(0, -1).join(' ');
        lastName = nameParts[nameParts.length - 1];
      }
    }
    
    // Ensure we have at least a firstName
    if (!firstName) {
      firstName = 'Customer';
      lastName = '';
    }
    
    // Parse address
    const addressParts = (quote.customer.address || '').split(',').map(part => part.trim());
    const addressData = {
      street1: addressParts[0] || "123 Main St",
      city: addressParts[1] || "Unknown",
      province: addressParts[2] || "Unknown",
      postalCode: addressParts[3] || "00000",
      country: "USA"
    };

    console.log('📝 Parsed data:', { firstName, lastName, addressData });

    try {
      // Step 1: Search for existing client
      console.log('🔍 STEP 1: Searching for existing client...');
      const existingClient = await this.findExistingClient(firstName, lastName, quote.customer.email, quote.customer.phone);
      
      let finalClientId, finalPropertyId;
      
      if (existingClient) {
        console.log('✅ Found existing client:', existingClient.name);
        finalClientId = existingClient.id;
        
        // Step 2: Check if property exists for this client
        console.log('🔍 STEP 2: Checking for existing property...');
        const existingProperty = await this.findExistingProperty(existingClient.id, addressData);
        
        if (existingProperty) {
          console.log('✅ Found existing property:', existingProperty.address.street1);
          finalPropertyId = existingProperty.id;
          console.log('📋 SCENARIO 3: Existing Client + Existing Property + New Quote');
        } else {
          console.log('➕ Creating new property for existing client...');
          finalPropertyId = await this.createPropertyForExistingClient(existingClient.id, addressData);
          console.log('📋 SCENARIO 2: Existing Client + New Property + New Quote');
        }
      } else {
        console.log('➕ No existing client found, creating new client with property...');
        const newClientData = await this.createNewClientWithProperty(firstName, lastName, quote.customer.email, quote.customer.phone, addressData);
        finalClientId = newClientData.clientId;
        finalPropertyId = newClientData.propertyId;
        console.log('📋 SCENARIO 1: New Client + New Property + New Quote');
      }
      
      // Step 3: Create quote with determined client and property
      console.log('🔨 STEP 3: Creating quote...');
      console.log('📊 Using Client ID:', finalClientId);
      console.log('📊 Using Property ID:', finalPropertyId);
      
      const createdQuote = await this.createQuoteForClientAndProperty(finalClientId, finalPropertyId, quote);
      
      console.log('🎯🎯🎯 COMPLETE SUCCESS: Smart sync completed!');
      return {
        success: true,
        clientId: finalClientId,
        propertyId: finalPropertyId,
        quoteId: createdQuote.id,
        message: 'Smart sync completed successfully!'
      };

    } catch (error) {
      console.error('🧠 Smart sync failed:', error);
      console.error('🧠 Error details:', error.message);
      
      // Enhanced error messages for different failure scenarios
      if (error.message.includes('Property creation failed')) {
        console.error('🏠 PROPERTY CREATION ERROR: Failed to create property for existing client');
        console.error('🏠 This usually means the address format is invalid or the client already has a property at this address');
      } else if (error.message.includes('Client creation failed')) {
        console.error('👤 CLIENT CREATION ERROR: Failed to create new client with property');
        console.error('👤 This usually means the client data is invalid or there are GraphQL field mismatches');
      } else if (error.message.includes('Quote creation failed')) {
        console.error('📄 QUOTE CREATION ERROR: Failed to create quote for client and property');
        console.error('📄 This usually means the line items format is invalid or there are missing required fields');
      } else if (error.message.includes('No properties found for client')) {
        console.error('🏠 PROPERTY SEARCH ERROR: No properties found for existing client');
        console.error('🏠 This usually means the client exists but has no properties, or the GraphQL query failed');
      } else if (error.message.includes('Error finding existing client')) {
        console.error('👤 CLIENT SEARCH ERROR: Failed to search for existing clients');
        console.error('👤 This usually means the GraphQL query failed or there are authentication issues');
      } else if (error.message.includes('Error finding existing property')) {
        console.error('🏠 PROPERTY SEARCH ERROR: Failed to search for existing properties');
        console.error('🏠 This usually means the GraphQL query failed or the client ID is invalid');
      } else {
        console.error('❓ UNKNOWN ERROR: Unexpected error during smart sync');
        console.error('❓ Error type:', error.constructor.name);
        console.error('❓ Error stack:', error.stack);
      }
      
      throw error;
    }
  }

  // Helper method: Find existing client by name + contact (with pagination).
  // Priority: 1) name + phone, 2) name + email, 3) name-only if exactly one match (Feature #6).
  static async findExistingClient(firstName, lastName, email, phone) {
    try {
      console.log('🔍 Searching for existing client with pagination:', { firstName, lastName, email, phone });
      
      const pageSize = 50;
      let hasNextPage = true;
      let after = null;
      let pageCount = 0;
      const nameOnlyMatches = []; // Collect name-only matches for fallback when no phone/email match
      
      while (hasNextPage) {
        pageCount++;
        console.log(`📄 Searching page ${pageCount}...`);
        
        const { data } = await jobberClient.query({
          query: JOBBER_QUERIES.GET_CUSTOMERS,
          variables: {
            first: pageSize,
            after: after
          }
        });
        
        if (!data || !data.clients || !data.clients.nodes) {
          console.log('❌ No client data returned from GraphQL query');
          break;
        }
        
        console.log(`📋 Page ${pageCount} response:`, {
          nodesCount: data.clients.nodes.length,
          totalCount: data.clients.totalCount,
          hasNextPage: data.clients.pageInfo?.hasNextPage
        });
        
        for (const client of data.clients.nodes) {
          const clientFirstName = (client.firstName || '').toLowerCase();
          const clientLastName = (client.lastName || '').toLowerCase();
          
          if (clientFirstName !== firstName.toLowerCase() || clientLastName !== lastName.toLowerCase()) {
            continue;
          }
          
          const clientRecord = {
            id: client.id,
            name: `${client.firstName} ${client.lastName}`.trim(),
            email: client.emails?.[0]?.address || '',
            phone: client.phones?.[0]?.number || '',
            address: client.billingAddress ? 
              `${client.billingAddress.street1}, ${client.billingAddress.city}, ${client.billingAddress.province} ${client.billingAddress.postalCode}`.trim() : 
              ''
          };
          
          // Priority 1: name + phone match
          if (phone) {
            const clientPhone = client.phones?.[0]?.number;
            if (clientPhone) {
              const normalizedClientPhone = clientPhone.replace(/\D/g, '');
              const normalizedQuotePhone = phone.replace(/\D/g, '');
              if (normalizedClientPhone === normalizedQuotePhone) {
                console.log('✅ Found matching client by name and phone:', { id: client.id, name: clientRecord.name, phone: clientPhone });
                return clientRecord;
              }
            }
          }
          
          // Priority 2: name + email match
          if (email) {
            const clientEmail = client.emails?.[0]?.address;
            if (clientEmail && clientEmail.toLowerCase() === email.toLowerCase()) {
              console.log('✅ Found matching client by name and email:', { id: client.id, name: clientRecord.name, email: clientEmail });
              return clientRecord;
            }
          }
          
          // Name matches but no phone/email match: collect for name-only fallback
          nameOnlyMatches.push(clientRecord);
        }
        
        const pageInfo = data.clients.pageInfo;
        hasNextPage = pageInfo?.hasNextPage || false;
        after = pageInfo?.endCursor || null;
        console.log(`✅ Page ${pageCount} checked. Name-only matches so far: ${nameOnlyMatches.length}. Has next page: ${hasNextPage}`);
      }
      
      // Feature #6: If no phone/email match but exactly one name-only match, use it
      if (nameOnlyMatches.length === 1) {
        console.log('✅ Found unique client by name only (no phone/email match):', nameOnlyMatches[0].name);
        return nameOnlyMatches[0];
      }
      if (nameOnlyMatches.length > 1) {
        console.log(`⚠️ Multiple Jobber clients match name "${firstName} ${lastName}". Sync will create new client.`);
      }
      
      console.log(`❌ No matching client found after checking ${pageCount} page(s)`);
      return null;
    } catch (error) {
      console.error('❌ Error finding existing client:', error);
      return null;
    }
  }

  // Helper method: Find existing property for client
  static async findExistingProperty(clientId, addressData) {
    try {
      console.log('🔍 findExistingProperty: Starting property search...');
      console.log('🔍 Client ID:', clientId);
      console.log('🔍 Address data to match:', addressData);
      
      const query = gql`
        query GetClientProperties($clientId: EncodedId!) {
          client(id: $clientId) {
            clientProperties {
              nodes {
                id
                address {
                  street1
                  city
                  province
                  postalCode
                  country
                }
              }
            }
          }
        }
      `;
      
      console.log('🔍 Executing GraphQL query for client properties...');
      const { data } = await jobberClient.query({
        query,
        variables: { clientId }
      });
      
      console.log('🔍 GraphQL query response:', data);
      
      if (!data || !data.client) {
        console.log('❌ No client data returned from GraphQL query');
        return null;
      }
      
      if (!data.client.clientProperties) {
        console.log('❌ No clientProperties in client data');
        return null;
      }
      
      const properties = data.client.clientProperties.nodes;
      console.log('🔍 Found properties for client:', properties);
      console.log('🔍 Number of properties:', properties.length);
      
      if (!properties || properties.length === 0) {
        console.log('❌ No properties found for client');
        return null;
      }
      
      console.log('🔍 Starting address comparison for each property...');
      for (let i = 0; i < properties.length; i++) {
        const property = properties[i];
        console.log(`🔍 Checking property ${i + 1}:`, property);
        console.log(`🔍 Property address:`, property.address);
        
        const isMatch = this.addressesMatch(property.address, addressData);
        console.log(`🔍 Property ${i + 1} match result:`, isMatch);
        
        if (isMatch) {
          console.log('✅ Found matching property!', property);
          return property;
        }
      }
      
      console.log('❌ No matching properties found after checking all properties');
      return null;
    } catch (error) {
      console.error('❌ Error finding existing property:', error);
      console.error('❌ Error details:', error.message);
      console.error('❌ Error stack:', error.stack);
      return null;
    }
  }

  // Helper method: Fuzzy address matching
  static addressesMatch(existingAddress, newAddress) {
    // Extract street number and name from both addresses
    const existing = this.parseStreetAddress(existingAddress.street1);
    const newAddr = this.parseStreetAddress(newAddress.street1);
    
    console.log('🔍 Address comparison:', {
      existing: existing,
      new: newAddr,
      numberMatch: existing.number === newAddr.number,
      nameMatch: this.streetNamesMatch(existing.name, newAddr.name)
    });
    
    // Street number must match exactly
    if (existing.number !== newAddr.number) return false;
    
    // Street name must match (with fuzzy logic)
    return this.streetNamesMatch(existing.name, newAddr.name);
  }

  // Helper method: Parse street address into number and name
  static parseStreetAddress(streetAddress) {
    const match = streetAddress.match(/^(\d+)\s+(.+)$/);
    if (match) {
      return {
        number: match[1],
        name: match[2].trim()
      };
    }
    return {
      number: '',
      name: streetAddress
    };
  }

  // Helper method: Fuzzy street name matching
  static streetNamesMatch(name1, name2) {
    // Normalize street types
    const normalize = (str) => {
      return str.toLowerCase()
        .replace(/\bavenue?\b/g, 'ave')
        .replace(/\bstreet?\b/g, 'st')
        .replace(/\broad?\b/g, 'rd')
        .replace(/\bdrive?\b/g, 'dr')
        .replace(/\bcircle?\b/g, 'cir')
        .replace(/\bcourt?\b/g, 'ct')
        .replace(/\bfirst?\b/g, '1st')
        .replace(/\bsecond?\b/g, '2nd')
        .replace(/\bthird?\b/g, '3rd')
        .replace(/\s+/g, ' ')
        .trim();
    };
    
    return normalize(name1) === normalize(name2);
  }

  // Helper method: Create property for existing client
  static async createPropertyForExistingClient(clientId, addressData) {
    try {
      const mutation = gql`
        mutation CreateProperty($clientId: EncodedId!, $input: PropertyCreateInput!) {
          propertyCreate(clientId: $clientId, input: $input) {
            properties {
              id
              address {
                street1
                city
                province
                postalCode
                country
              }
            }
            userErrors {
              message
              path
            }
          }
        }
      `;

      const { data } = await jobberClient.mutate({
        mutation,
        variables: {
          clientId,
          input: {
            properties: [
              {
                address: addressData
              }
            ]
          }
        }
      });

      if (data.propertyCreate.userErrors.length > 0) {
        throw new Error(`Property creation failed: ${data.propertyCreate.userErrors.map(e => `${e.path}: ${e.message}`).join(", ")}`);
      }

      console.log('✅ Created new property for existing client:', data.propertyCreate.properties);
      return data.propertyCreate.properties[0].id;
    } catch (error) {
      console.error('❌ Error creating property for existing client:', error);
      throw error;
    }
  }

  // Helper method: Create new client with property (original working approach)
  static async createNewClientWithProperty(firstName, lastName, email, phone, addressData) {
    try {
      const clientMutation = gql`
        mutation CreateClient($input: ClientCreateInput!) {
          clientCreate(input: $input) {
            client {
              id
              firstName
              lastName
              name
              clientProperties {
                nodes {
                  id
                  address {
                    street1
                    city
                    province
                    postalCode
                    country
                  }
                }
              }
            }
            userErrors { path message }
          }
        }
      `;

      const clientInput = {
        firstName: firstName,
        lastName: lastName,
        emails: email ? [{ address: email }] : [],
        phones: phone ? [{ number: phone }] : [],
        isCompany: false,
        properties: [
          {
            address: addressData
          }
        ]
      };

      const { data } = await jobberClient.mutate({
        mutation: clientMutation,
        variables: { input: clientInput }
      });

      if (data.clientCreate.userErrors.length > 0) {
        throw new Error(`Client creation failed: ${data.clientCreate.userErrors.map(e => `${e.path}: ${e.message}`).join(", ")}`);
      }

      const client = data.clientCreate.client;
      const property = client.clientProperties.nodes[0];

      console.log('✅ Created new client with property:', { client: client.name, property: property.address.street1 });
      
      return {
        clientId: client.id,
        propertyId: property.id
      };
    } catch (error) {
      console.error('❌ Error creating new client with property:', error);
      throw error;
    }
  }

  // Translator: Convert Squeekit line items to Jobber format using Squeekit's logic
  static translateSqueekitToJobberLineItems(quote) {
    // Check if we have multiple structures
    const hasMultipleStructures = quote.structures && quote.structures.length > 1;
    
    // Get window types from localStorage or use default structure
    let windowTypes = [];
    try {
      const stored = localStorage.getItem('windowTypes');
      if (stored) {
        windowTypes = JSON.parse(stored);
      } else {
        // Fallback to default window types structure
        windowTypes = [
          { id: 1, name: 'Standard', price: 10, category: 'exterior_interior' },
          { id: 2, name: 'Double Sized', price: 15, category: 'exterior_interior' },
          { id: 3, name: 'French', price: 12, category: 'exterior_interior' },
          { id: 4, name: 'Screens', price: 5, category: 'separate' },
          { id: 5, name: 'Garage Door', price: 20, category: 'exterior_interior' },
          { id: 6, name: 'Skylight', price: 8, category: 'separate' }
        ];
      }
    } catch (error) {
      console.warn('⚠️ Could not load window types, using defaults:', error);
      windowTypes = [
        { id: 1, name: 'Standard', price: 10, category: 'exterior_interior' },
        { id: 2, name: 'Double Sized', price: 15, category: 'exterior_interior' },
        { id: 3, name: 'French', price: 12, category: 'exterior_interior' },
        { id: 4, name: 'Screens', price: 5, category: 'separate' },
        { id: 5, name: 'Garage Door', price: 20, category: 'exterior_interior' },
        { id: 6, name: 'Skylight', price: 8, category: 'separate' }
      ];
    }
    
    // Apply percentage split from quote settings (default to 60/40 if not set)
    const exteriorPercentage = quote.settings?.exteriorPercentage || 60;
    const interiorPercentage = quote.settings?.interiorPercentage || 40;
    const showExterior = quote.settings?.showExterior !== false; // default true
    const showInterior = quote.settings?.showInterior !== false; // default true
    
    // Build Jobber line items array
    const jobberLineItems = [];
    
    if (hasMultipleStructures) {
      // Process each structure separately
      console.log('🏗️ Processing multiple structures...');
      
      for (const structure of quote.structures) {
        const structureLineItems = structure.lineItems || [];
        console.log(`📋 Processing structure "${structure.name}" with ${structureLineItems.length} line items`);
        
        // Filter line items by category for this structure
        const exteriorInteriorItems = structureLineItems.filter(item => {
          const windowType = windowTypes.find(w => w.name === item.windowType);
          const isExteriorInterior = windowType?.category === 'exterior_interior';
          return isExteriorInterior;
        });
        
        const separateItems = structureLineItems.filter(item => {
          const windowType = windowTypes.find(w => w.name === item.windowType);
          const isSeparate = windowType?.category === 'separate';
          return isSeparate;
        });
        
        // Calculate totals for this structure
        const exteriorInteriorSubtotal = exteriorInteriorItems.reduce((sum, item) => sum + (item.price || 0), 0);
        const exteriorTotal = exteriorInteriorSubtotal * (exteriorPercentage / 100);
        const interiorTotal = exteriorInteriorSubtotal * (interiorPercentage / 100);
        
        console.log(`💰 Structure "${structure.name}" totals:`, { exteriorTotal, interiorTotal });
        
        // Add Exterior Windows for this structure (if enabled and has value)
        if (showExterior && exteriorTotal > 0) {
          jobberLineItems.push({
            name: `Exterior - ${structure.name}`,
            description: `Exterior window cleaning services - ${structure.name}`,
            quantity: 1,
            unitPrice: exteriorTotal,
            saveToProductsAndServices: false
          });
          console.log(`✅ Added Exterior - ${structure.name}:`, exteriorTotal);
        }
        
        // Add Interior Windows for this structure (if enabled and has value)
        if (showInterior && interiorTotal > 0) {
          jobberLineItems.push({
            name: `Interior - ${structure.name}`,
            description: `Interior window cleaning services - ${structure.name}`,
            quantity: 1,
            unitPrice: interiorTotal,
            saveToProductsAndServices: false
          });
          console.log(`✅ Added Interior - ${structure.name}:`, interiorTotal);
        }
        
        // Add separate items for this structure
        separateItems.forEach(item => {
          const unitPrice = (item.totalCount && item.totalCount > 0) ? item.price / item.totalCount : item.price;
          jobberLineItems.push({
            name: `${item.windowType} - ${structure.name}`,
            description: `${item.windowType} - ${structure.name}`,
            quantity: item.totalCount || 1,
            unitPrice: unitPrice,
            saveToProductsAndServices: false
          });
        });
      }
      
      // Add Extra Dirty Fee as separate line item if applicable (only add once for all structures)
      if (quote.totals && quote.totals.extraDirtyAmount && quote.totals.extraDirtyAmount > 0) {
        jobberLineItems.push({
          name: "Extra Dirty Fee",
          description: "Additional fee for extra dirty windows",
          quantity: 1,
          unitPrice: quote.totals.extraDirtyAmount,
          saveToProductsAndServices: false
        });
      }
    } else {
      // Single structure or no structures - use current logic
      // Get line items - use structures if available, otherwise use quote.lineItems
      const lineItems = (quote.structures && quote.structures.length > 0) 
        ? quote.structures[0].lineItems || []
        : quote.lineItems || [];
      
      // Filter line items by category (using Squeekit's exact logic)
      const exteriorInteriorItems = lineItems.filter(item => {
        const windowType = windowTypes.find(w => w.name === item.windowType);
        const isExteriorInterior = windowType?.category === 'exterior_interior';
        return isExteriorInterior;
      });
      
      const separateItems = lineItems.filter(item => {
        const windowType = windowTypes.find(w => w.name === item.windowType);
        const isSeparate = windowType?.category === 'separate';
        return isSeparate;
      });
      
      // Calculate totals using Squeekit's exact logic
      const exteriorInteriorSubtotal = exteriorInteriorItems.reduce((sum, item) => sum + (item.price || 0), 0);
      const exteriorTotal = exteriorInteriorSubtotal * (exteriorPercentage / 100);
      const interiorTotal = exteriorInteriorSubtotal * (interiorPercentage / 100);
      
      // Add Exterior Windows (if enabled and has value)
      if (showExterior && exteriorTotal > 0) {
        jobberLineItems.push({
          name: "Exterior Windows",
          description: "Exterior window cleaning services",
          quantity: 1,
          unitPrice: exteriorTotal,
          saveToProductsAndServices: false
        });
      }
      
      // Add Interior Windows (if enabled and has value)
      if (showInterior && interiorTotal > 0) {
        jobberLineItems.push({
          name: "Interior Windows",
          description: "Interior window cleaning services", 
          quantity: 1,
          unitPrice: interiorTotal,
          saveToProductsAndServices: false
        });
      }
      
      // Add separate items as individual line items
      separateItems.forEach(item => {
        const unitPrice = (item.totalCount && item.totalCount > 0) ? item.price / item.totalCount : item.price;
        jobberLineItems.push({
          name: item.windowType,
          description: item.windowType,
          quantity: item.totalCount || 1,
          unitPrice: unitPrice,
          saveToProductsAndServices: false
        });
      });
      
      // Add Extra Dirty Fee as separate line item if applicable
      if (quote.totals && quote.totals.extraDirtyAmount && quote.totals.extraDirtyAmount > 0) {
        jobberLineItems.push({
          name: "Extra Dirty Fee",
          description: "Additional fee for extra dirty windows",
          quantity: 1,
          unitPrice: quote.totals.extraDirtyAmount,
          saveToProductsAndServices: false
        });
      }
    }
    
    return jobberLineItems;
  }

  // Format pricing notes for Jobber quote note
  static formatPricingNotes(quote) {
    const structures = quote.structures || (quote.lineItems ? [{ name: 'Main', lineItems: quote.lineItems }] : []);
    
    // Calculate prices per structure (similar to QuoteDetail.js logic)
    const structurePrices = structures.map(structure => {
      const lineItems = structure.lineItems || [];
      const windowTypeData = {};
      
      lineItems.forEach(item => {
        if (!item.windowType) return;
        
        const count = item.totalCount || item.quantity || 0;
        if (count === 0) return;
        
        // Calculate price per unit: use override if present, otherwise calculate from price/totalCount
        let pricePerUnit;
        if (item.pricePerUnitOverride != null) {
          pricePerUnit = item.pricePerUnitOverride;
        } else {
          // Calculate backwards from total price
          pricePerUnit = count > 0 ? (item.price || 0) / count : 0;
        }
        
        if (!windowTypeData[item.windowType]) {
          windowTypeData[item.windowType] = {
            count: 0,
            pricePerUnit: pricePerUnit,
            prices: [] // Track all prices to detect variations
          };
        }
        windowTypeData[item.windowType].count += count;
        windowTypeData[item.windowType].prices.push(pricePerUnit);
      });
      
      // Normalize prices - if all items of same type have same price, use that
      // Otherwise, if there are variations, we'll use the first one (shouldn't happen in practice)
      Object.keys(windowTypeData).forEach(windowType => {
        const data = windowTypeData[windowType];
        const uniquePrices = [...new Set(data.prices.map(p => p.toFixed(2)))];
        if (uniquePrices.length === 1) {
          data.pricePerUnit = data.prices[0];
        } else {
          // Multiple prices - use first (this shouldn't happen if price override applies to all)
          data.pricePerUnit = data.prices[0];
        }
        delete data.prices; // Clean up
      });
      
      return {
        name: structure.name,
        windowTypes: windowTypeData
      };
    }).filter(s => Object.keys(s.windowTypes).length > 0);
    
    if (structurePrices.length === 0) return '';
    
    let notes = 'Prices used for this quote:\n\n';
    
    structurePrices.forEach(structure => {
      notes += `${structure.name}\n`;
      Object.entries(structure.windowTypes)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .forEach(([windowTypeName, data]) => {
          notes += `${windowTypeName} - ${data.count}\n`;
          notes += `$${Number(data.pricePerUnit).toFixed(2)} per unit\n`;
        });
      notes += '\n';
    });
    
    return notes.trim();
  }

  // Helper method: Create quote for client and property
  static async createQuoteForClientAndProperty(clientId, propertyId, quote) {
    try {
      const quoteMutation = gql`
        mutation CreateQuote($attributes: QuoteCreateAttributes!) {
          quoteCreate(attributes: $attributes) {
            quote {
              id
              title
              client { id name }
              property { id }
              lineItems {
                nodes {
                  id
                  name
                  description
                  quantity
                  unitPrice
                  totalPrice
                  unitCost
                  totalCost
                }
              }
            }
            userErrors {
              message
              path
            }
          }
        }
      `;

      // Translate Squeekit line items to Jobber format using Squeekit's logic
      const lineItems = this.translateSqueekitToJobberLineItems(quote);

      const quoteAttributes = {
        clientId: clientId,
        propertyId: propertyId,
        title: `Window Cleaning Quote - ${new Date().toLocaleDateString()}`,
        message: "Professional window cleaning services",
        lineItems: lineItems
      };

      console.log('📝 Quote attributes:', quoteAttributes);
      const { data } = await jobberClient.mutate({
        mutation: quoteMutation,
        variables: { attributes: quoteAttributes }
      });

      if (data.quoteCreate.userErrors.length > 0) {
        throw new Error(`Quote creation failed: ${data.quoteCreate.userErrors.map(e => `${e.path}: ${e.message}`).join(", ")}`);
      }

      console.log('✅ Quote created successfully:', data.quoteCreate.quote);
      const createdQuote = data.quoteCreate.quote;
      
      // Create quote note with pricing info and user notes
      try {
        await this.createQuoteNote(createdQuote.id, quote);
      } catch (noteError) {
        // Log error but don't fail the quote creation if note creation fails
        console.warn('⚠️ Failed to create quote note (quote was created successfully):', noteError);
      }
      
      return createdQuote;
    } catch (error) {
      console.error('❌ Error creating quote:', error);
      throw error;
    }
  }

  // Create a note for a quote (internal notes, not visible to customer)
  static async createQuoteNote(quoteId, quote) {
    try {
      // Format pricing notes
      const pricingNotes = this.formatPricingNotes(quote);
      
      // Combine with user notes
      let combinedNotes = '';
      if (pricingNotes) {
        combinedNotes = pricingNotes;
      }
      if (quote.notes && quote.notes.trim()) {
        if (combinedNotes) {
          combinedNotes += '\n\n';
        }
        combinedNotes += quote.notes.trim();
      }
      
      // Only create note if we have content
      if (!combinedNotes || !combinedNotes.trim()) {
        return;
      }
      
      const { data } = await jobberClient.mutate({
        mutation: JOBBER_MUTATIONS.CREATE_QUOTE_NOTE,
        variables: {
          quoteId: quoteId,
          input: {
            message: combinedNotes,
            linkedTo: {
              invoices: false,
              jobs: false
            }
          }
        }
      });
      
      if (data.quoteCreateNote.userErrors && data.quoteCreateNote.userErrors.length > 0) {
        console.error('❌ Error creating quote note:', data.quoteCreateNote.userErrors);
        throw new Error(`Failed to create quote note: ${data.quoteCreateNote.userErrors.map(e => `${e.path}: ${e.message}`).join(", ")}`);
      }
      
      return data.quoteCreateNote.quoteNote;
    } catch (error) {
      console.error('❌ Error creating quote note:', error);
      console.error('❌ Error details:', error.message);
      if (error.networkError) {
        console.error('❌ Network error:', error.networkError);
      }
      if (error.graphQLErrors) {
        console.error('❌ GraphQL errors:', error.graphQLErrors);
      }
      throw error;
    }
  }
}
